import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Color definitions for each portal
const colorPalettes = {
  'Main App (Customer Portal)': {
    description: 'Primary colors used in the customer-facing application',
    colors: [
      { name: 'Primary', hex: '#3B82F6', tailwind: 'blue-500', usage: 'Buttons, links, active states' },
      { name: 'Primary Light', hex: '#60A5FA', tailwind: 'blue-400', usage: 'Hover states' },
      { name: 'Primary Dark', hex: '#2563EB', tailwind: 'blue-600', usage: 'Active/pressed states' },
      { name: 'Background', hex: '#0F172A', tailwind: 'slate-900', usage: 'Main background' },
      { name: 'Background Alt', hex: '#0a0a0b', tailwind: 'custom', usage: 'Sidebar background (dark mode)' },
      { name: 'Card Background', hex: '#1E293B', tailwind: 'slate-800', usage: 'Cards, panels' },
      { name: 'Border', hex: '#334155', tailwind: 'slate-700', usage: 'Borders, dividers' },
      { name: 'Text Primary', hex: '#F8FAFC', tailwind: 'slate-50', usage: 'Primary text' },
      { name: 'Text Muted', hex: '#94A3B8', tailwind: 'slate-400', usage: 'Secondary text' },
      { name: 'Success', hex: '#22C55E', tailwind: 'green-500', usage: 'Success states, online status' },
      { name: 'Warning', hex: '#F59E0B', tailwind: 'amber-500', usage: 'Warning states, pending' },
      { name: 'Error', hex: '#EF4444', tailwind: 'red-500', usage: 'Error states, destructive actions' },
    ]
  },
  'Landing & Public Pages': {
    description: 'Colors used on the landing page, pricing, and marketing pages',
    colors: [
      { name: 'Gradient Start', hex: '#06B6D4', tailwind: 'cyan-500', usage: 'Primary gradient start' },
      { name: 'Gradient End', hex: '#2563EB', tailwind: 'blue-600', usage: 'Primary gradient end' },
      { name: 'Accent Cyan', hex: '#22D3EE', tailwind: 'cyan-400', usage: 'Highlights, badges' },
      { name: 'Background Dark', hex: '#020617', tailwind: 'slate-950', usage: 'Page background' },
      { name: 'Background Mid', hex: '#0F172A', tailwind: 'slate-900', usage: 'Section backgrounds' },
      { name: 'Card Background', hex: '#1E293B', tailwind: 'slate-800/30', usage: 'Feature cards' },
      { name: 'Border', hex: '#334155', tailwind: 'slate-700/50', usage: 'Card borders' },
      { name: 'Text White', hex: '#FFFFFF', tailwind: 'white', usage: 'Headlines' },
      { name: 'Text Gray', hex: '#94A3B8', tailwind: 'slate-400', usage: 'Body text' },
      { name: 'Text Muted', hex: '#64748B', tailwind: 'slate-500', usage: 'Captions' },
    ]
  },
  'Partner Portal': {
    description: 'Colors used in the partner/reseller portal',
    colors: [
      { name: 'Primary', hex: '#3B82F6', tailwind: 'blue-500', usage: 'Primary actions' },
      { name: 'Primary Light', hex: '#60A5FA', tailwind: 'blue-400', usage: 'Hover states' },
      { name: 'Background', hex: '#0F172A', tailwind: 'slate-900', usage: 'Main background' },
      { name: 'Card Background', hex: '#1E293B', tailwind: 'slate-800', usage: 'Cards, sidebar' },
      { name: 'Success/Active', hex: '#22C55E', tailwind: 'green-500', usage: 'Active status badges' },
      { name: 'Commission Highlight', hex: '#10B981', tailwind: 'emerald-500', usage: 'Earnings displays' },
      { name: 'Border', hex: '#334155', tailwind: 'slate-700', usage: 'Borders' },
      { name: 'Text Primary', hex: '#F8FAFC', tailwind: 'slate-50', usage: 'Primary text' },
      { name: 'Text Muted', hex: '#94A3B8', tailwind: 'slate-400', usage: 'Secondary text' },
    ]
  },
  'Admin Portal': {
    description: 'Colors used in the platform administration portal',
    colors: [
      { name: 'Gradient Start', hex: '#EF4444', tailwind: 'red-500', usage: 'Admin gradient start' },
      { name: 'Gradient End', hex: '#EA580C', tailwind: 'orange-600', usage: 'Admin gradient end' },
      { name: 'Accent Red', hex: '#F87171', tailwind: 'red-400', usage: 'Active nav items, highlights' },
      { name: 'Background Dark', hex: '#0F172A', tailwind: 'slate-900', usage: 'Main background' },
      { name: 'Sidebar Background', hex: '#1E293B', tailwind: 'slate-800/50', usage: 'Sidebar' },
      { name: 'Card Border', hex: '#334155', tailwind: 'slate-700', usage: 'Card borders' },
      { name: 'Input Background', hex: '#0F172A', tailwind: 'slate-900/50', usage: 'Form inputs' },
      { name: 'Text White', hex: '#FFFFFF', tailwind: 'white', usage: 'Headlines, primary text' },
      { name: 'Text Gray', hex: '#94A3B8', tailwind: 'slate-400', usage: 'Secondary text' },
      { name: 'Text Muted', hex: '#64748B', tailwind: 'slate-500', usage: 'Placeholders' },
    ]
  },
  'Status & Feedback Colors': {
    description: 'Consistent colors used across all portals for status indicators',
    colors: [
      { name: 'Success', hex: '#22C55E', tailwind: 'green-500', usage: 'Success messages, online, active' },
      { name: 'Success Light', hex: '#86EFAC', tailwind: 'green-300', usage: 'Success backgrounds' },
      { name: 'Warning', hex: '#F59E0B', tailwind: 'amber-500', usage: 'Warnings, pending states' },
      { name: 'Warning Light', hex: '#FCD34D', tailwind: 'amber-300', usage: 'Warning backgrounds' },
      { name: 'Error', hex: '#EF4444', tailwind: 'red-500', usage: 'Errors, destructive actions' },
      { name: 'Error Light', hex: '#FCA5A5', tailwind: 'red-300', usage: 'Error backgrounds' },
      { name: 'Info', hex: '#3B82F6', tailwind: 'blue-500', usage: 'Information, tips' },
      { name: 'Info Light', hex: '#93C5FD', tailwind: 'blue-300', usage: 'Info backgrounds' },
    ]
  },
  'Plan Badge Colors': {
    description: 'Colors used for subscription plan indicators',
    colors: [
      { name: 'Free Plan', hex: '#6B7280', tailwind: 'gray-500', usage: 'Free tier badge' },
      { name: 'Free Background', hex: '#6B7280/20', tailwind: 'gray-500/20', usage: 'Free badge bg' },
      { name: 'Pro Plan', hex: '#06B6D4', tailwind: 'cyan-500', usage: 'Pro tier badge' },
      { name: 'Pro Background', hex: '#06B6D4/20', tailwind: 'cyan-500/20', usage: 'Pro badge bg' },
    ]
  }
};

function ColorSwatch({ color }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`Copied ${text}`);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle colors with opacity
  const displayHex = color.hex.includes('/') ? color.hex.split('/')[0] : color.hex;

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors group">
      {/* Color swatch */}
      <div 
        className="w-12 h-12 rounded-lg border border-slate-600 shrink-0 shadow-inner"
        style={{ backgroundColor: displayHex }}
      />
      
      {/* Color info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white">{color.name}</span>
          <Badge variant="outline" className="text-xs font-mono bg-slate-900/50">
            {color.tailwind}
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-0.5 truncate">{color.usage}</p>
      </div>
      
      {/* Hex value with copy button */}
      <button
        onClick={() => copyToClipboard(color.hex)}
        className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900/50 hover:bg-slate-900 transition-colors group-hover:border-slate-500 border border-transparent"
      >
        <span className="font-mono text-sm text-slate-300">{color.hex}</span>
        {copied ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-slate-500 group-hover:text-slate-300" />
        )}
      </button>
    </div>
  );
}

export default function AdminColors() {
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Color Palette</h1>
        <p className="text-slate-400 mt-1">
          Reference guide for colors used across all CONNECT portals
        </p>
      </div>

      {/* Color Palettes */}
      <div className="space-y-8">
        {Object.entries(colorPalettes).map(([paletteName, palette]) => (
          <Card key={paletteName} className="bg-slate-800/30 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white">{paletteName}</CardTitle>
              <CardDescription className="text-slate-400">
                {palette.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {palette.colors.map((color, idx) => (
                  <ColorSwatch key={idx} color={color} />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CSS Variables Reference */}
      <Card className="mt-8 bg-slate-800/30 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white">CSS Variables (shadcn/ui)</CardTitle>
          <CardDescription className="text-slate-400">
            Theme variables defined in the application's CSS
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre className="text-slate-300">
{`:root {
  --background: 222.2 84% 4.9%;
  --foreground: 210 40% 98%;
  --card: 222.2 84% 4.9%;
  --card-foreground: 210 40% 98%;
  --popover: 222.2 84% 4.9%;
  --popover-foreground: 210 40% 98%;
  --primary: 217.2 91.2% 59.8%;
  --primary-foreground: 222.2 47.4% 11.2%;
  --secondary: 217.2 32.6% 17.5%;
  --secondary-foreground: 210 40% 98%;
  --muted: 217.2 32.6% 17.5%;
  --muted-foreground: 215 20.2% 65.1%;
  --accent: 217.2 32.6% 17.5%;
  --accent-foreground: 210 40% 98%;
  --destructive: 0 62.8% 30.6%;
  --destructive-foreground: 210 40% 98%;
  --border: 217.2 32.6% 17.5%;
  --input: 217.2 32.6% 17.5%;
  --ring: 224.3 76.3% 48%;
}`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
